import { Badge } from "@/lib/components/Badge";
import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { Input } from "@/lib/components/Input";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { cn } from "@/lib/utils";
import { FontDto } from "@repo/api/font";
import { IconCheck, IconDotsVertical, IconSearch } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { categoryLabels } from "./font-utils";
import { FontCategoryFilter } from "./FontFilters";

type FontListViewProps = {
  fonts: FontDto[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedExternalId?: string;
  onUseFont: (font: FontDto) => void;
  onDeleteFont: (font: FontDto) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
};

export default function FontListView({
  fonts,
  searchQuery,
  onSearchChange,
  selectedExternalId,
  onUseFont,
  onDeleteFont,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onLoadMore,
}: FontListViewProps) {
  return (
    <>
      <SearchBar searchQuery={searchQuery} onSearchChange={onSearchChange} />
      <FontList
        fonts={fonts}
        selectedExternalId={selectedExternalId}
        onUseFont={onUseFont}
        onDeleteFont={onDeleteFont}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
      />
    </>
  );
}

function SearchBar({
  searchQuery,
  onSearchChange,
}: {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}) {
  return (
    <div className="px-8 pt-6">
      <Input
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search fonts..."
        leadingComponent={<IconSearch className="size-4 text-text-secondary" />}
      />
    </div>
  );
}

function FontList({
  fonts,
  selectedExternalId,
  onUseFont,
  onDeleteFont,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onLoadMore,
}: {
  fonts: FontDto[];
  selectedExternalId?: string;
  onUseFont: (font: FontDto) => void;
  onDeleteFont: (font: FontDto) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}) {
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      onLoadMore();
    }
  }, [inView, hasNextPage, isFetchingNextPage, onLoadMore]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center px-8 py-12">
        <LoadingSpinner size={24} />
      </div>
    );
  }

  if (fonts.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-8 py-12">
        <IconSearch className="mb-3 text-text-primary" size={32} stroke={1.5} />
        <p className="mb-0.5 text-sm font-medium text-text-secondary">
          No fonts found
        </p>
        <p className="text-sm text-text-secondary">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <div className="relative grow overflow-hidden">
      <div className="absolute inset-0 flex flex-col gap-1 overflow-y-auto px-8 py-3 pb-8">
        {fonts.map((font) => (
          <FontRow
            key={font.externalId}
            font={font}
            isSelected={font.externalId === selectedExternalId}
            onUseFont={onUseFont}
            onDeleteFont={onDeleteFont}
          />
        ))}
        {hasNextPage && <div ref={ref} className="h-px shrink-0" />}
        {isFetchingNextPage && (
          <div className="flex justify-center py-3">
            <LoadingSpinner size={20} />
          </div>
        )}
      </div>
    </div>
  );
}

function FontRow({
  font,
  isSelected,
  onUseFont,
  onDeleteFont,
}: {
  font: FontDto;
  isSelected: boolean;
  onUseFont: (font: FontDto) => void;
  onDeleteFont: (font: FontDto) => void;
}) {
  const isTeamFont = font.teamExternalId !== null;
  const [menuOpen, setMenuOpen] = useState(false);

  const categoryLabel = font.metadata.category
    ? categoryLabels[font.metadata.category as FontCategoryFilter]
    : undefined;

  const showActionsClass = cn(
    "hidden group-focus-within:inline-flex group-hover:inline-flex",
    menuOpen && "inline-flex",
  );

  return (
    <div
      className={cn(
        "group relative -mx-3 flex items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-100 hover:bg-grey-100",
        menuOpen && "bg-grey-100",
      )}
    >
      <div className="h-13 w-52 rounded">
        <img
          src={font.metadata.previewSrc}
          alt={font.family}
          className="rounded border border-gray-100 bg-gray-50 object-contain"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden text-left">
        <span className="truncate text-sm font-medium text-text-primary">
          {font.family}
        </span>
        {categoryLabel ? (
          <span className="truncate text-xs text-text-secondary">
            {categoryLabel}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {isTeamFont && !menuOpen ? (
          <Badge
            variant="outline"
            className="bg-white group-focus-within:hidden group-hover:hidden"
          >
            Team font
          </Badge>
        ) : null}
        {isSelected && !menuOpen ? (
          <span
            className="mx-2 flex items-center justify-center group-focus-within:hidden group-hover:hidden [&_svg]:stroke-[url(#icon-gradient)]"
            aria-label="Selected"
          >
            <IconCheck size={20} stroke={2.5} />
          </span>
        ) : null}
        {isTeamFont ? (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="iconSm"
                className={showActionsClass}
                aria-label={`Options for ${font.family}`}
              >
                <IconDotsVertical />
              </Button>
            </DropdownMenuTrigger>
            {menuOpen && (
              <DropdownMenuContent
                className="w-40"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuItem
                  className="text-error hover:text-error!"
                  onSelect={() => onDeleteFont(font)}
                >
                  Delete font
                </DropdownMenuItem>
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className={showActionsClass}
          onClick={() => onUseFont(font)}
        >
          Use
        </Button>
      </div>
    </div>
  );
}
